const QUESTION_TYPES = new Set(['select', 'multiselect', 'yes_no', 'text', 'acknowledge']);

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function isOption(option) {
  return (
    option &&
    typeof option === 'object' &&
    isNonEmptyString(option.label) &&
    ['string', 'number', 'boolean'].includes(typeof option.value) &&
    (option.disabled === undefined || typeof option.disabled === 'boolean')
  );
}

function isQuestion(question) {
  if (!question || typeof question !== 'object') return false;
  if (!isNonEmptyString(question.id) || !/^[A-Za-z0-9_-]+$/.test(question.id)) return false;
  if (!isNonEmptyString(question.question)) return false;
  if (!QUESTION_TYPES.has(question.type)) return false;

  const options = Array.isArray(question.options) ? question.options : [];
  if ((question.type === 'select' || question.type === 'multiselect') && options.length === 0) {
    return false;
  }
  if (question.type !== 'select' && question.type !== 'multiselect' && options.length > 0) {
    return false;
  }
  return options.every(isOption);
}

export function normalizeClarificationQuestions(questions = []) {
  return questions.map((question, index) => {
    const hasOptions = Array.isArray(question?.options) && question.options.length > 0;
    const type = question?.type || (hasOptions ? 'select' : 'text');
    return {
      ...question,
      id: question?.id || `question_${index + 1}`,
      type,
      required: question?.required ?? true,
      options: hasOptions ? question.options : undefined,
    };
  });
}

export function isClarificationRequest(value) {
  return (
    value &&
    typeof value === 'object' &&
    value.needs_clarification === true &&
    Array.isArray(value.clarification_questions) &&
    value.clarification_questions.length > 0 &&
    value.clarification_questions.every(isQuestion)
  );
}

function labelForAnswer(question, answerValue) {
  const options = Array.isArray(question.options) ? question.options : [];
  const found = options.find((option) => option.value === answerValue);
  return found?.label ?? (typeof answerValue === 'string' ? answerValue : undefined);
}

export function buildClarificationContext({
  parentQueryId,
  specialistRunId = null,
  originalQuery,
  questions,
  answers,
  metadata = {},
}) {
  const normalizedAnswers = {};

  for (const question of questions || []) {
    if (!question?.id || !(question.id in (answers || {}))) continue;
    const value = answers[question.id];
    normalizedAnswers[question.id] = {
      value,
      label: Array.isArray(value)
        ? value.map((item) => labelForAnswer(question, item) ?? String(item)).join(', ')
        : labelForAnswer(question, value),
    };
  }

  return {
    schema_version: '1.0',
    parent_query_id: parentQueryId,
    specialist_run_id: specialistRunId,
    original_query: originalQuery,
    rounds: [
      {
        questions: (questions || []).map((question) => ({
          id: question.id,
          type: question.type,
        })),
        answers: normalizedAnswers,
      },
    ],
    metadata,
  };
}

export function extractDeveloperTrace(payload = {}) {
  const trace = payload.trace || payload.developer_trace || payload.specialist_trace;
  if (!trace || typeof trace !== 'object' || trace.developer_only !== true) {
    return null;
  }
  return trace;
}
